import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFilterConfigs } from '../../../../hooks/useFilterConfigs';
import { useFilters } from '../../../../hooks/useFilters';
import type { Filter } from '../../../../stores/filters';
import type { FilterConfigItem } from '../components/FilterConfig/FilterConfigTypes';

interface LocalFilter extends Filter {
  _cachedExpr?: string;
}

const applyFilterChange = (
  filter: LocalFilter,
  prev: Record<string, LocalFilter>,
  mainFilterName: string,
  filtersList: Filter[],
  filterConfigs: FilterConfigItem[],
): Record<string, LocalFilter> => {
  const updated: Record<string, LocalFilter> = {
    ...prev,
    [filter.filterName]: filter,
  };

  const depCfg = filterConfigs.find((c) => c.filterName === mainFilterName);
  if (!depCfg) return updated;

  const { secondaryFilters = [], auxiliaryFilters = [] } = depCfg;

  const hasOtherExpressions = filterConfigs
    .filter((c) => c.filterName !== mainFilterName)
    .some((c) => {
      const local = updated[c.filterName];
      const fromStore = filtersList.find((f) => f.filterName === c.filterName);

      return local?.expression?.trim() || (fromStore as Filter | undefined)?.expression?.trim();
    });

  const names = hasOtherExpressions ? secondaryFilters : [...secondaryFilters, ...auxiliaryFilters];

  const isMain = filter.filterName === mainFilterName;
  const isEmpty = filter.expression.trim() === '';
  const mainFilterWasEmpty = prev[mainFilterName]?.expression.trim() === '';

  if (!isMain) {
    return updated;
  }

  if (isEmpty) {
    names.forEach((n) => {
      const prevDep = updated[n] ?? (filtersList.find((f) => f.filterName === n) as Filter | undefined);

      updated[n] = {
        ...(prevDep ?? { filterName: n, enabled: false, expression: '' }),
        _cachedExpr: prevDep?.expression ?? '',
        expression: '',
        enabled: false,
      };
    });
  } else if (mainFilterWasEmpty) {
    names.forEach((depName) => {
      const cachedExpr = prev[depName]?._cachedExpr;
      const storedFilter = filtersList.find((f) => f.filterName === depName);

      if (cachedExpr !== undefined) {
        updated[depName] = {
          ...(storedFilter ?? {
            filterName: depName,
            enabled: !!cachedExpr,
            expression: cachedExpr,
          }),
          expression: cachedExpr,
          enabled: !!cachedExpr,
        };
      } else if (storedFilter) {
        updated[depName] = storedFilter;
      }
    });
  }

  return updated;
};

export const useSubGroupFilterState = (
  mainFilterName: string,
  onChange?: (filters: Record<string, Filter>) => void,
) => {
  const [filters, setFilters] = useState<Record<string, LocalFilter>>({});
  const filterState = useFilters();
  const filtersList = useMemo(() => Object.values(filterState?.filters ?? {}) as Filter[], [filterState?.filters]);
  const { filterConfigs } = useFilterConfigs();
  const depCfg = useMemo(
    () => filterConfigs.find((c) => c.filterName === mainFilterName),
    [filterConfigs, mainFilterName],
  );

  const handleChange = useCallback(
    (filter: Filter) =>
      setFilters((prev) => applyFilterChange(filter as LocalFilter, prev, mainFilterName, filtersList, filterConfigs)),
    [filtersList, mainFilterName, filterConfigs],
  );

  useEffect(() => {
    if (!onChange) return;

    const mainFilter = filters[mainFilterName];
    if (!mainFilter) return;

    const mainExpr = mainFilter.expression?.trim() ?? '';

    if (mainExpr === '') {
      const secondaryFilters = depCfg?.secondaryFilters ?? [];
      const auxiliaryFilters = depCfg?.auxiliaryFilters ?? [];

      const secondariesEmpty = Object.entries(filters).every(
        ([name, f]) => name === mainFilterName || !secondaryFilters.includes(name) || f.expression?.trim() === '',
      );
      if (!secondariesEmpty) return;

      const filtersWithoutAuxiliaries = Object.fromEntries(
        Object.entries(filters).filter(([name]) => !auxiliaryFilters.includes(name)),
      );

      onChange(filtersWithoutAuxiliaries);
      return;
    }

    const allHaveExpr = Object.entries(filters)
      .filter(([name]) => name !== mainFilterName)
      .every(([, f]) => !!f.expression?.trim());
    if (!allHaveExpr) return;

    onChange(filters);
  }, [filters, depCfg, mainFilterName, onChange]);

  return { filters, handleChange };
};
